import { seedAdminIfNeeded } from '../services/auth/seedAdmin';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { setUnauthorizedHandler } from '../api/authSession';
import { tokenStorage } from '../api/tokenStorage';
import { authService } from '../services';
import type { AuthContextType, User } from '../types';
import type { ChangePasswordRequest, UpdateProfileRequest } from '../types/api';
import type { RegistrationConsents } from '../types/consents';
import { clearPendingVerifyEmail } from '../utils/emailVerification';
import { isProtectedAppPath } from '../utils/authRoutes';

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    seedAdminIfNeeded();
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      const path = window.location.pathname;
      if (isProtectedAppPath(path)) {
        window.location.assign('/login');
      }
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const currentUser = await authService.getCurrentUser();
        if (!cancelled) setUser(currentUser);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string) => {
    const { user: nextUser, token } = await authService.login({ email, password });
    tokenStorage.set(token);
    clearPendingVerifyEmail();
    setUser(nextUser);
  };

  const register = async (
    name: string,
    email: string,
    password: string,
    consents: RegistrationConsents,
  ) => {
    return authService.register({ name, email, password, consents });
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      tokenStorage.clear();
      setUser(null);
    }
  };

  const updateProfile = async (data: UpdateProfileRequest) => {
    const updatedUser = await authService.updateProfile(data);
    setUser(updatedUser);
    return updatedUser;
  };

  const changePassword = async (data: ChangePasswordRequest) => {
    return authService.changePassword(data);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        updateProfile,
        changePassword,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
